
import React from 'react';
import type { CustomApprovalStep } from '../../types/legalWorkLog';
import { useAuth } from '../../context/AuthContext';
import { isLegal, isLisa, isIrene } from '../../config/roles';
import firebase from 'firebase/compat/app';

export const INITIAL_APPROVAL_STEPS: CustomApprovalStep[] = [
    {
        id: 'step1',
        order: 1,
        type: 'single',
        approverUid: 'group_legal', // mapped to isLegal() check
        approverName: '法務 (KC)',
        status: 'pending',
        decidedAt: null,
        comment: null
    },
    {
        id: 'step2',
        order: 2,
        type: 'parallel_all',
        approvers: [
            { uid: 'lisa', name: 'Lisa', status: 'pending', decidedAt: null, comment: null },
            { uid: 'irene', name: 'Irene', status: 'pending', decidedAt: null, comment: null }
        ]
    }
];

interface Props {
    title?: string;
    steps: CustomApprovalStep[];
    overallStatus: string;
    onUpdate: (newSteps: CustomApprovalStep[], newStatus: string) => void;
    canSubmit: boolean; // Is user the owner/assignee?
    onStatusChange?: (status: string) => void; // e.g. move todo status
}

export function SimpleApprovalSection({ title = "簽核流程", steps, overallStatus, onUpdate, canSubmit, onStatusChange }: Props) {
    const { user } = useAuth();
    const userEmail = user?.email;

    // Helper: Determine if user can act on a step
    const canActOnStep = (step: CustomApprovalStep): boolean => {
        if (overallStatus !== 'in_review') return false;
        
        // Find current active step index (first pending)
        const activeIndex = steps.findIndex(s => {
            if (s.type === 'single') return s.status === 'pending';
            if (s.type === 'parallel_all') return s.approvers.some(a => a.status === 'pending');
            return false;
        });
        
        // If no pending, flow is done
        if (activeIndex === -1) return false;
        
        // Only allow action if this step is the active one
        if (steps[activeIndex].id !== step.id) return false;

        // Check Identity
        if (step.type === 'single') {
            return isLegal(userEmail); // Specific logic for step 1 (KC)
        } 
        if (step.type === 'parallel_all') {
            // User can act if they match one of the approvers AND that approver is pending
            if (isLisa(userEmail)) return step.approvers.find(a => a.uid === 'lisa')?.status === 'pending' || false;
            if (isIrene(userEmail)) return step.approvers.find(a => a.uid === 'irene')?.status === 'pending' || false;
        }
        return false;
    };

    const handleAction = (stepIndex: number, decision: 'approved' | 'rejected', subUid?: string) => {
        const comment = prompt(`請輸入${decision === 'approved' ? '核准' : '駁回'}意見：`);
        if (comment === null) return; // Cancel

        const newSteps = [...steps];
        const step = { ...newSteps[stepIndex] }; // Shallow copy step
        const now = firebase.firestore.Timestamp.now();

        if (step.type === 'single') {
            step.status = decision;
            step.decidedAt = now;
            step.comment = comment;
            newSteps[stepIndex] = step;
        } else {
            // Parallel
            step.approvers = step.approvers.map(a => {
                if (a.uid === subUid) {
                    return { ...a, status: decision, decidedAt: now, comment };
                }
                return a;
            });
            newSteps[stepIndex] = step;
        }

        // Evaluate Workflow State
        let newOverallStatus = overallStatus;
        
        if (decision === 'rejected') {
            newOverallStatus = 'rejected';
            if (onStatusChange) onStatusChange('in_progress'); // Revert todo status
        } else {
            // Check if ALL steps done
            const allDone = newSteps.every(s => {
                if (s.type === 'single') return s.status === 'approved';
                return s.approvers.every(a => a.status === 'approved');
            });
            if (allDone) {
                newOverallStatus = 'approved';
                if (onStatusChange) onStatusChange('closed');
            }
        }

        onUpdate(newSteps, newOverallStatus);
    };

    const startReview = () => {
        if (onStatusChange) onStatusChange('ready_for_review');
        onUpdate(steps, 'in_review');
    };

    return (
        <div className="section" style={{background:'rgba(255,255,255,0.02)', padding:16, borderRadius:8}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
                <div style={{fontWeight:'bold', color:'#8b5cf6'}}>{title}</div>
                {canSubmit && (overallStatus === 'not_started' || overallStatus === 'rejected') && (
                    <button className="smallbtn" style={{background:'#3b82f6', color:'white'}} onClick={startReview}>
                        {overallStatus === 'rejected' ? '重新送簽' : '送出簽核'}
                    </button>
                )}
                {overallStatus === 'in_review' && <span className="badge" style={{color:'#fbbf24', borderColor:'#fbbf24'}}>簽核中</span>}
                {overallStatus === 'approved' && <span className="badge" style={{color:'var(--ok)', borderColor:'var(--ok)'}}>已核准</span>}
                {overallStatus === 'rejected' && <span className="badge" style={{color:'var(--danger)', borderColor:'var(--danger)'}}>已駁回</span>}
            </div>

            <div style={{display:'flex', flexDirection:'column', gap:12}}>
                {steps.map((step, idx) => {
                    const isPending = canActOnStep(step);
                    
                    return (
                        <div key={step.id} style={{display:'flex', alignItems:'center', gap:10, opacity: overallStatus === 'not_started' ? 0.5 : 1}}>
                            <div style={{width:24, height:24, borderRadius:'50%', background:'#333', color:'#888', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12}}>
                                {step.order}
                            </div>
                            <div style={{flex:1, background:'#222', padding:'8px 12px', borderRadius:6, border: isPending ? '1px solid #8b5cf6' : '1px solid #333'}}>
                                {step.type === 'single' ? (
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                        <div>
                                            <div style={{fontSize:13, fontWeight:'bold'}}>{step.approverName}</div>
                                            {step.comment && <div style={{fontSize:12, color:'#aaa'}}>"{step.comment}"</div>}
                                        </div>
                                        {step.status === 'pending' ? (
                                            isPending ? (
                                                <div style={{display:'flex', gap:4}}>
                                                    <button className="smallbtn" style={{color:'var(--ok)', borderColor:'var(--ok)'}} onClick={() => handleAction(idx, 'approved')}>同意</button>
                                                    <button className="smallbtn" style={{color:'var(--danger)', borderColor:'var(--danger)'}} onClick={() => handleAction(idx, 'rejected')}>駁回</button>
                                                </div>
                                            ) : <span style={{fontSize:12, color:'#666'}}>待審核</span>
                                        ) : (
                                            <span style={{fontSize:12, color: step.status==='approved'?'var(--ok)':'var(--danger)'}}>
                                                {step.status==='approved'?'已同意':'已駁回'}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    // Parallel
                                    <div>
                                        <div style={{fontSize:13, fontWeight:'bold', marginBottom:4}}>主管會簽</div>
                                        {step.approvers.map(approver => {
                                            const subPending = overallStatus === 'in_review' && approver.status === 'pending' && ((approver.uid==='lisa' && isLisa(userEmail)) || (approver.uid==='irene' && isIrene(userEmail)));
                                            const isStepActive = canActOnStep(step); // Check if this step is logically active first

                                            return (
                                                <div key={approver.uid} style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:4, borderTop:'1px dashed #333', paddingTop:4}}>
                                                    <div>
                                                        <span style={{fontSize:12}}>{approver.name}</span>
                                                        {approver.comment && <span style={{fontSize:11, color:'#aaa', marginLeft:6}}>"{approver.comment}"</span>}
                                                    </div>
                                                    {approver.status === 'pending' ? (
                                                        (subPending && isStepActive) ? (
                                                            <div style={{display:'flex', gap:4}}>
                                                                <button className="smallbtn" style={{fontSize:10, padding:'2px 6px', color:'var(--ok)', borderColor:'var(--ok)'}} onClick={() => handleAction(idx, 'approved', approver.uid)}>同意</button>
                                                                <button className="smallbtn" style={{fontSize:10, padding:'2px 6px', color:'var(--danger)', borderColor:'var(--danger)'}} onClick={() => handleAction(idx, 'rejected', approver.uid)}>駁回</button>
                                                            </div>
                                                        ) : <span style={{fontSize:11, color:'#666'}}>待審核</span>
                                                    ) : (
                                                        <span style={{fontSize:11, color: approver.status==='approved'?'var(--ok)':'var(--danger)'}}>
                                                            {approver.status==='approved'?'已同意':'已駁回'}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
